<template>
  <el-dialog
    :append-to-body="true"
    :visible="true"
    width="90%"
    custom-class="sticky-footer-dialog"
    :close-on-click-modal="false"
    :before-close="goBack"
  >
    <div slot="title">
      {{ readOnly ? 'Raklap megtekintése' : 'Raklap szerkesztése' }}
      <template v-if="pallet">
        – {{ pallet.position }}. raklap
      </template>
      <el-tag
        v-if="pallet && readOnly"
        size="small"
        type="success"
        style="margin-left: 8px;"
      >
        Véglegesítve
      </el-tag>
    </div>

    <div
      v-if="!pallet"
      v-loading="loading"
      style="min-height: 120px;"
    />

    <template v-else>
      <!-- pallet header data -->
      <el-form label-position="top">
        <el-row :gutter="20">
          <el-col
            v-if="readOnly"
            :span="headerSpan"
          >
            <el-form-item label="SSCC">
              <el-input
                :value="pallet.sscc"
                disabled
              />
            </el-form-item>
          </el-col>
          <el-col :span="headerSpan">
            <el-form-item
              label="Raklap típusa *"
              :error="palletProductError"
            >
              <el-select
                v-model="form.pallet_product_id"
                :disabled="readOnly"
                placeholder="Válasszon raklapot..."
                style="width:100%;"
              >
                <el-option
                  v-for="option in palletProducts"
                  :key="option.id"
                  :label="option.name"
                  :value="option.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="headerSpan">
            <el-form-item
              label="Bruttó tömeg (kg) *"
              :error="weightError"
            >
              <el-input-number
                v-model="form.gross_weight"
                :disabled="readOnly"
                :min="0"
                :precision="2"
                style="width:100%;"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <!-- items on this pallet (rows are added from the coverage table below) -->
      <h4>Raklap tételei</h4>

      <el-table
        :data="rows"
        size="small"
        style="width:100%; margin-bottom: 24px;"
      >
        <el-table-column
          label="Termék"
          min-width="220"
        >
          <template slot-scope="scope">
            <el-select
              v-model="scope.row.product_pack_type_id"
              filterable
              :disabled="readOnly"
              placeholder="Válasszon a listáról..."
              style="width:100%;"
              @change="(val) => onPackSelected(scope.row, val)"
            >
              <el-option
                v-for="opt in distinctPacks"
                :key="opt.product_pack_type_id"
                :label="opt.name + ' · ' + opt.pack_type_name"
                :value="opt.product_pack_type_id"
              />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column
          label="Beszállító"
          min-width="200"
        >
          <template slot-scope="scope">
            <el-select
              v-model="scope.row.supplier_id"
              filterable
              :disabled="readOnly || !scope.row.product_pack_type_id"
              placeholder="Beszállító..."
              style="width:100%;"
              @change="(val) => onSupplierSelected(scope.row, val)"
            >
              <el-option
                v-for="s in scope.row._suppliers"
                :key="s.id"
                :label="'#' + s.id + ' ' + (s.company && s.company.name)"
                :value="s.id"
              />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column
          label="Szavatosság"
          width="170"
        >
          <template slot-scope="scope">
            <el-select
              v-model="scope.row.expiration_date"
              clearable
              :disabled="readOnly || !scope.row.supplier_id"
              placeholder="Szavatosság..."
              style="width:100%;"
            >
              <el-option
                v-for="e in scope.row._expirations"
                :key="e.expiration_date"
                :label="formatDate(e.expiration_date)"
                :value="e.expiration_date"
              />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column
          label="Mennyiség"
          width="150"
        >
          <template slot-scope="scope">
            <el-input-number
              v-model="scope.row.count"
              :disabled="readOnly"
              :min="0"
              :max="maxForRow(scope.row)"
              :precision="0"
              size="small"
              style="width:100%;"
            />
          </template>
        </el-table-column>
        <el-table-column
          label="Elérhető"
          width="100"
          align="center"
        >
          <template slot-scope="scope">
            <el-tag
              v-if="scope.row.product_pack_type_id"
              size="mini"
              :type="maxForRow(scope.row) > 0 ? 'info' : 'danger'"
            >
              max {{ maxForRow(scope.row) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          v-if="!readOnly"
          width="60"
        >
          <template slot-scope="scope">
            <el-button
              type="text"
              icon="el-icon-delete"
              style="color:#F56C6C;"
              @click="removeRow(scope.$index)"
            />
          </template>
        </el-table-column>
      </el-table>

      <!-- live coverage feedback -->
      <h4>Lefedettség (élő)</h4>
      <el-table
        :data="liveCoverage"
        size="small"
        style="width:100%;"
      >
        <el-table-column
          label="Termék"
          prop="name"
          min-width="180"
        />
        <el-table-column
          label="Csomagolás"
          prop="pack_type_name"
          width="120"
        />
        <el-table-column
          label="Szavatosság"
          width="120"
        >
          <template slot-scope="scope">
            {{ scope.row.expiration_date || '—' }}
          </template>
        </el-table-column>
        <el-table-column
          label="Beszállító"
          width="150"
        >
          <template slot-scope="scope">
            {{ scope.row.supplier_name || '—' }}
          </template>
        </el-table-column>
        <el-table-column
          label="Listán"
          width="80"
          align="right"
        >
          <template slot-scope="scope">
            {{ scope.row.required }}
          </template>
        </el-table-column>
        <el-table-column
          label="Más raklapon"
          width="110"
          align="right"
        >
          <template slot-scope="scope">
            {{ scope.row.placedOther }}
          </template>
        </el-table-column>
        <el-table-column
          label="Ezen a raklapon"
          width="120"
          align="right"
        >
          <template slot-scope="scope">
            {{ scope.row.onThis }}
          </template>
        </el-table-column>
        <el-table-column
          label="Állapot"
          width="170"
        >
          <template slot-scope="scope">
            <el-tag
              v-if="scope.row.ok"
              size="small"
              type="success"
            >
              Kész
            </el-tag>
            <el-tag
              v-else
              size="small"
              type="warning"
            >
              Hátralévő: {{ scope.row.remaining }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          v-if="!readOnly"
          label=""
          width="60"
          align="center"
        >
          <template slot-scope="scope">
            <el-button
              v-if="!scope.row.ok"
              type="success"
              icon="el-icon-plus"
              circle
              size="mini"
              title="Hozzáadás a raklaphoz"
              @click="addFromCoverage(scope.row)"
            />
          </template>
        </el-table-column>
      </el-table>
    </template>

    <div
      v-if="pallet"
      slot="footer"
      class="dialog-footer"
    >
      <el-button @click="goBack">
        {{ readOnly ? 'Vissza' : 'Mégse' }}
      </el-button>
      <template v-if="!readOnly">
        <el-button
          type="success"
          :loading="saving"
          @click="save"
        >
          Raklap mentése
        </el-button>
        <el-button
          type="warning"
          :loading="finalizing"
          @click="finalize"
        >
          Véglegesítés
        </el-button>
      </template>
    </div>
  </el-dialog>
</template>

<script>
export default {
  props: {
    palletId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      finalizing: false,
      pallet: null,
      sourceItems: [],
      storageId: null,
      stockByProduct: {},
      // Selectable pallet products (active, flagged as pallets) from the master data.
      palletProducts: [],
      form: {
        gross_weight: 0,
        pallet_product_id: null,
      },
      rows: [],
      rowUid: 0,
      weightError: '',
      palletProductError: '',
    };
  },
  computed: {
    // Finalized pallets are locked: view-only, no edits.
    readOnly() {
      return !!(this.pallet && this.pallet.finalized_at);
    },
    headerSpan() {
      return this.readOnly ? 8 : 12;
    },
    // Distinct product+pack types on the loading list (for the manual add select).
    distinctPacks() {
      const seen = {};
      const list = [];
      (this.sourceItems || []).forEach((it) => {
        if (!seen[it.product_pack_type_id]) {
          seen[it.product_pack_type_id] = true;
          list.push({
            id: it.id, // edi_desadv_item_id of the source snapshot row
            product_id: it.product_id,
            product_pack_type_id: it.product_pack_type_id,
            name: it.name,
            pack_type_name: it.pack_type_name,
            count_per_pack: it.count_per_pack,
          });
        }
      });
      return list;
    },
    // Coverage placed on OTHER pallets (across all desadvs of the order), keyed by product_pack_type_id.
    coverageMap() {
      const map = {};
      (this.pallet && this.pallet.coverage || []).forEach((c) => {
        map[c.key] = c;
      });
      return map;
    },
    // Product-level required (summed across expiries/suppliers) — for the placement limit.
    productRequired() {
      const map = {};
      (this.sourceItems || []).forEach((it) => {
        map[it.product_pack_type_id] = (map[it.product_pack_type_id] || 0) + (it.count || 0);
      });
      return map;
    },
    // Product-level packs already placed on other pallets — for the placement limit.
    productPlacedOther() {
      const map = {};
      (this.pallet && this.pallet.coverage || []).forEach((c) => {
        map[c.product_pack_type_id] = (map[c.product_pack_type_id] || 0) + (c.placed || 0);
      });
      return map;
    },
    // Live coverage per source item, keyed by product_pack_type_id; completed rows sorted to the bottom.
    liveCoverage() {
      const rows = (this.sourceItems || []).map((item) => {
        const cov = this.coverageMap[item.product_pack_type_id] || {};
        const required = cov.required != null ? cov.required : item.count;
        const placedOther = cov.placed || 0;
        const onThis = this.sumRowsForPack(item.product_pack_type_id);
        const remaining = required - placedOther - onThis;
        return {
          key: item.product_pack_type_id,
          source: item,
          name: item.name,
          pack_type_name: item.pack_type_name,
          expiration_date: item.expiration_date,
          supplier_name: item.supplier_name,
          required,
          placedOther,
          onThis,
          remaining: remaining > 0 ? remaining : 0,
          ok: remaining <= 0,
        };
      });
      return rows
        .map((r, i) => ({ r, i }))
        .sort((a, b) => (a.r.ok === b.r.ok ? a.i - b.i : (a.r.ok ? 1 : -1)))
        .map(x => x.r);
    },
  },
  async mounted() {
    await this.load();
  },
  methods: {
    formatDate(d) {
      return d ? String(d).substr(0, 10) : '';
    },
    // ---- data loading ----
    async load() {
      this.loading = true;
      try {
        const pallet = await this.$store.dispatch('desadv/fetchPallet', this.palletId);
        this.pallet = pallet;
        this.sourceItems = pallet.items || [];
        this.storageId = pallet.storage_id || null;
        this.palletProducts = pallet.pallet_products || [];
        this.form = {
          gross_weight: pallet.gross_weight || 0,
          pallet_product_id: pallet.pallet_product_id || null,
        };
        this.rows = (pallet.pallet_items || []).map(item => ({
          uid: this.rowUid++,
          product_id: item.product_id,
          product_pack_type_id: item.product_pack_type_id,
          edi_desadv_item_id: item.edi_desadv_item_id || null,
          name: item.name,
          pack_type_name: item.pack_type_name,
          count_per_pack: item.count_per_pack,
          count: item.count,
          supplier_id: item.supplier_id || null,
          supplier_name: item.supplier_name || null,
          expiration_date: item.expiration_date ? item.expiration_date.substr(0, 10) : null,
          _suppliers: [],
          _expirations: [],
        }));
        await Promise.all(this.rows.map(r => this.hydrateRowStock(r)));
      } catch (e) {
      }
      this.loading = false;
    },
    // ---- stock tree (supplier -> expiration), mirroring the CustomerSale flow ----
    async fetchStock(productId) {
      if (this.stockByProduct[productId]) return this.stockByProduct[productId];
      if (!this.storageId) return [];
      try {
        const stock = await axios.get(`api/products/${productId}/storage/${this.storageId}`);
        const packs = (stock && stock.packs) || [];
        this.$set(this.stockByProduct, productId, packs);
        return packs;
      } catch (e) {
        return [];
      }
    },
    async hydrateRowStock(row) {
      if (!row.product_id) return;
      const packs = await this.fetchStock(row.product_id);
      const pack = (packs || []).find(p => p.id === row.product_pack_type_id);
      row._suppliers = (pack && pack.suppliers) || [];
      this.ensureSupplierOption(row);
      const supplier = row._suppliers.find(s => s.id === row.supplier_id);
      row._expirations = (supplier && supplier.expirations) || [];
      this.ensureExpirationOption(row);
    },
    // Keep the inherited supplier/expiry visible even if that lot is no longer in stock.
    ensureSupplierOption(row) {
      if (row.supplier_id && !row._suppliers.find(s => s.id === row.supplier_id)) {
        row._suppliers = [
          { id: row.supplier_id, company: { name: row.supplier_name || ('#' + row.supplier_id) }, count: null, expirations: [] },
          ...row._suppliers,
        ];
      }
    },
    ensureExpirationOption(row) {
      const exp = row.expiration_date;
      if (exp && !row._expirations.find(e => this.formatDate(e.expiration_date) === this.formatDate(exp))) {
        row._expirations = [{ expiration_date: exp, count: null }, ...row._expirations];
      }
    },
    pickMaxStock(list) {
      if (!list || !list.length) return null;
      return list.reduce((best, cur) => ((cur.count || 0) > (best && best.count || 0) ? cur : best), list[0]);
    },
    // ---- row editing ----
    async onPackSelected(row, packTypeId) {
      const src = this.distinctPacks.find(p => p.product_pack_type_id === packTypeId);
      if (!src) return;
      row.product_id = src.product_id;
      row.product_pack_type_id = src.product_pack_type_id;
      row.edi_desadv_item_id = src.id; // provenance ref into the desadv snapshot
      row.name = src.name;
      row.pack_type_name = src.pack_type_name;
      row.count_per_pack = src.count_per_pack;
      row.supplier_id = null;
      row.supplier_name = null;
      row.expiration_date = null;
      const packs = await this.fetchStock(row.product_id);
      const pack = (packs || []).find(p => p.id === packTypeId);
      row._suppliers = (pack && pack.suppliers) || [];
      const supplier = this.pickMaxStock(row._suppliers);
      if (supplier) {
        this.onSupplierSelected(row, supplier.id);
        const exp = this.pickMaxStock(row._expirations);
        row.expiration_date = exp ? exp.expiration_date : (row._expirations[0] && row._expirations[0].expiration_date) || null;
      }
      const max = this.maxForRow(row);
      if (!row.count || row.count > max) {
        row.count = max > 0 ? max : 1;
      }
    },
    onSupplierSelected(row, supplierId) {
      const supplier = (row._suppliers || []).find(s => s.id === supplierId);
      row.supplier_id = supplierId;
      row.supplier_name = supplier ? (supplier.company && supplier.company.name) : null;
      row._expirations = (supplier && supplier.expirations) || [];
      if (!row._expirations.find(e => this.formatDate(e.expiration_date) === this.formatDate(row.expiration_date))) {
        row.expiration_date = row._expirations.length ? row._expirations[0].expiration_date : null;
      }
    },
    sumRowsForPack(packTypeId, excludeUid = null) {
      return this.rows
        .filter(r => r.product_pack_type_id === packTypeId && r.uid !== excludeUid)
        .reduce((acc, r) => acc + (r.count || 0), 0);
    },
    // Placement limit is product-level (summed across lots/suppliers).
    maxForRow(row) {
      if (!row.product_pack_type_id) return 0;
      const required = this.productRequired[row.product_pack_type_id] || 0;
      const placedOther = this.productPlacedOther[row.product_pack_type_id] || 0;
      const others = this.sumRowsForPack(row.product_pack_type_id, row.uid);
      const max = required - placedOther - others;
      return max > 0 ? max : 0;
    },
    async addFromCoverage(covRow) {
      const src = covRow.source;
      const row = {
        uid: this.rowUid++,
        product_id: src.product_id,
        product_pack_type_id: src.product_pack_type_id,
        edi_desadv_item_id: src.id || null, // provenance ref into the desadv snapshot
        name: src.name,
        pack_type_name: src.pack_type_name,
        count_per_pack: src.count_per_pack,
        count: 1,
        supplier_id: src.supplier_id || null,
        supplier_name: src.supplier_name || null,
        expiration_date: src.expiration_date || null,
        _suppliers: [],
        _expirations: [],
      };
      this.rows.push(row);
      await this.hydrateRowStock(row);
      const max = this.maxForRow(row);
      const want = covRow.remaining > 0 ? covRow.remaining : 1;
      row.count = max > 0 ? Math.min(want, max) : want;
    },
    removeRow(index) {
      this.rows.splice(index, 1);
    },
    // The parent DESADV dialog owns the layer: it closes it and reloads its own data.
    goBack() {
      this.$emit('close');
    },
    buildItemsPayload() {
      return this.rows
        .filter(r => r.product_pack_type_id && r.count > 0)
        .map(r => ({
          product_id: r.product_id,
          product_pack_type_id: r.product_pack_type_id,
          edi_desadv_item_id: r.edi_desadv_item_id,
          supplier_id: r.supplier_id,
          count: r.count,
          count_per_pack: r.count_per_pack,
          expiration_date: r.expiration_date,
          name: r.name,
          pack_type_name: r.pack_type_name,
          supplier_name: r.supplier_name,
        }));
    },
    persist() {
      return this.$store.dispatch('desadv/updatePallet', {
        id: this.pallet.id,
        payLoad: {
          gross_weight: this.form.gross_weight,
          pallet_product_id: this.form.pallet_product_id,
          items: this.buildItemsPayload(),
        },
      });
    },
    async save() {
      if (!this.validateHeader() || !this.validateRows()) {
        return;
      }
      this.saving = true;
      try {
        await this.persist();
        this.goBack();
      } catch (e) {
      }
      this.saving = false;
    },
    // The pallet type drives the stock movement at truck close, so it is required
    // just like the weight.
    validateHeader() {
      this.weightError = '';
      this.palletProductError = '';
      if (!this.form.gross_weight || this.form.gross_weight <= 0) {
        this.weightError = 'A raklap tömegének megadása kötelező.';
        return false;
      }
      if (!this.form.pallet_product_id) {
        this.palletProductError = 'A raklap típusának megadása kötelező.';
        return false;
      }
      return true;
    },
    // Supplier and expiration identify the lot, so every placed row must have both.
    validateRows() {
      const incomplete = this.buildItemsPayload().find(r => !r.supplier_id || !r.expiration_date);
      if (incomplete) {
        this.$message.warning('Minden tételnél kötelező a beszállító és a szavatossági idő megadása.');
        return false;
      }
      return true;
    },
    async finalize() {
      if (!this.validateHeader() || !this.validateRows()) {
        return;
      }
      if (this.buildItemsPayload().length < 1) {
        this.$message.warning('Üres raklap nem véglegesíthető.');
        return;
      }
      try {
        await this.$confirm(
          'A véglegesítés kiosztja a raklap SSCC kódját, és a raklap többé nem szerkeszthető. Folytatja?',
          'Raklap véglegesítése',
          { confirmButtonText: 'Véglegesítés', cancelButtonText: 'Mégse', type: 'warning' },
        );
      } catch (e) {
        return; // cancelled
      }
      this.finalizing = true;
      try {
        await this.persist();
        await this.$store.dispatch('desadv/finalizePallet', this.pallet.id);
        this.goBack();
      } catch (e) {
      }
      this.finalizing = false;
    },
  },
};
</script>
